import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './UsersManagement.module.css';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash,
  UserPlus,
  Users as UsersIcon,
  Shield,
  User,
  Phone,
  Mail,
  Home,
  Building,
  Filter,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Settings,
  MapPin,
  Loader,
  MoreVertical,
  UserMinus,
  AlertTriangle,
  X,
  Eye,
  EyeOff,
  Columns,
  RotateCcw,
  Database,
  Plus,
  Check,
  BarChart3
} from 'lucide-react';
import {
  getListUsers,
  getUsersWithCustomColumns,
  getUserDeletionOptions,
  executeUserDeletionAction,
  removeUserFromApartment,
  removeManagerRole,
  updateVaadMonthlyForBuilding
} from '../../Fetch/fetchUser';
import { getRelatedTablesSchema } from '../../Fetch/fetchTableSchema';
import UserForm from './UserForm';
import ExcelUpload from './ExcelUpload';

// User Details Modal Component
const UserDetailsModal = ({ isOpen, onClose, user }) => {
  if (!isOpen || !user) return null;

  const getRoleDisplay = (user) => {
    const roles = [];
    if (user.IsManager === "כן") roles.push('מנהל');
    if (user.IsVaad === "כן") roles.push('חבר ועד');
    if (user.IsTenant === "כן") roles.push('דייר');
    return roles.length > 0 ? roles.join(', ') : 'לא מוגדר';
  };

  const getRoleBadgeClass = (user) => {
    if (user.IsManager === "כן") return styles.roleAdmin;
    if (user.IsVaad === "כן") return styles.roleCommittee;
    if (user.IsTenant === "כן") return styles.roleResident;
    return styles.roleDefault;
  };

  return (
    <div className={styles.userModalContent}>
      {/* Header with gradient background */}
      <div className={styles.userModalHeader}>
        <div className={styles.userHeaderInfo}>
          <div className={styles.userAvatar}>
            <User size={32} />
          </div>
          <div className={styles.userHeaderText}>
            <h2 className={styles.userName}>{user.FullName}</h2>
            <span className={`${styles.userRole} ${getRoleBadgeClass(user)}`}>
              {getRoleDisplay(user)}
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className={styles.userModalCloseButton}
          aria-label="סגור"
        >
          ×
        </button>
      </div>
        
        {/* Content sections */}
        <div className={styles.userModalBody}>
          {/* Contact Information */}
          <div className={styles.detailSection}>
            <h3 className={styles.sectionTitle}>
              <Mail size={20} />
              פרטי התקשרות
            </h3>
            <div className={styles.sectionContent}>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Mail size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>דוא"ל</span>
                  <span className={styles.detailValue}>{user.Email || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Phone size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>טלפון</span>
                  <span className={styles.detailValue}>{user.Phone || 'לא צוין'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Apartment Information */}
          <div className={styles.detailSection}>
            <h3 className={styles.sectionTitle}>
              <Home size={20} />
              פרטי הדירה
            </h3>
            <div className={styles.sectionContent}>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Home size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>מספר דירה</span>
                  <span className={styles.detailValue}>{user.ApartmentNumber || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Building size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>קומה</span>
                  <span className={styles.detailValue}>{user.FloorNumber || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <UsersIcon size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>סוג קשר</span>
                  <span className={styles.detailValue}>{user.RelationshipType || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <MapPin size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>סכום חודשי</span>
                  <span className={styles.detailValueHighlight}>₪{user.VaadMonthly || 0}</span>
                </div>
              </div>
              {user.ParkingNumber && (
                <div className={styles.detailRow}>
                  <div className={styles.detailIcon}>
                    <MapPin size={16} />
                  </div>
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>חניות</span>
                    <span className={styles.detailValue}>{user.ParkingNumber}</span>
                  </div>
                </div>
              )}
              {user.ChargingStation !== undefined && user.ChargingStation !== null && (
                <div className={styles.detailRow}>
                  <div className={styles.detailIcon}>
                    <MapPin size={16} />
                  </div>
                  <div className={styles.detailInfo}>
                    <span className={styles.detailLabel}>עמדת טעינה</span>
                    <span className={styles.detailValue}>{user.ChargingStation ? 'כן' : 'לא'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Owner Information - Only for tenants */}
          {user.RelationshipType === 'שוכר' && (user.OwnerName || user.OwnerPhone) && (
            <div className={styles.detailSection}>
              <h3 className={styles.sectionTitle}>
                <Shield size={20} />
                פרטי בעלים
              </h3>
              <div className={styles.sectionContent}>
                {user.OwnerName && (
                  <div className={styles.detailRow}>
                    <div className={styles.detailIcon}>
                      <User size={16} />
                    </div>
                    <div className={styles.detailInfo}>
                      <span className={styles.detailLabel}>שם בעלים</span>
                      <span className={styles.detailValue}>{user.OwnerName}</span>
                    </div>
                  </div>
                )}
                {user.OwnerPhone && (
                  <div className={styles.detailRow}>
                    <div className={styles.detailIcon}>
                      <Phone size={16} />
                    </div>
                    <div className={styles.detailInfo}>
                      <span className={styles.detailLabel}>טלפון בעלים</span>
                      <span className={styles.detailValue}>{user.OwnerPhone}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System Information */}
          <div className={styles.detailSection}>
            <h3 className={styles.sectionTitle}>
              <Settings size={20} />
              מידע מערכת
            </h3>
            <div className={styles.sectionContent}>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <User size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>מזהה משתמש</span>
                  <span className={styles.detailValueMuted}>{user.UserID || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Home size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>מזהה דירה</span>
                  <span className={styles.detailValueMuted}>{user.ApartmentID || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <MapPin size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>עיר</span>
                  <span className={styles.detailValue}>{user.City || 'לא צוין'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className={styles.userModalFooter}>
          <button
            onClick={onClose}
            className={styles.userCloseButton}
          >
            <Settings size={16} />
            סגור
          </button>
        </div>
      </div>
  );
};

// Enhanced Smart Deletion Modal
const SmartDeletionModal = ({ isOpen, onClose, user, buildingId, apartmentId, onSuccess }) => {
  const [deletionOptions, setDeletionOptions] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadDeletionOptions();
    }
  }, [isOpen, user, buildingId, apartmentId]);

  const loadDeletionOptions = async () => {
    try {
      setLoading(true);
      const options = await getUserDeletionOptions(user, buildingId, apartmentId);
      setDeletionOptions(options.options || []);
    } catch (error) {
      console.error('Error loading deletion options:', error);
      alert('שגיאה בטעינת אפשרויות המחיקה: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    
    // Show confirmation for high-severity actions
    if (action.severity === 'critical' || action.severity === 'high') {
      setShowConfirmation(true);
      setConfirmText('');
    } else {
      executeAction(action);
    }
  };

  const executeAction = async (action) => {
    try {
      setLoading(true);
      
      const result = await executeUserDeletionAction(
        action.action,
        user.UserID,
        buildingId,
        apartmentId
      );

      onSuccess?.(result, action);
      onClose?.();
      
    } catch (error) {
      console.error('Error executing deletion action:', error);
      alert('שגיאה בביצוע הפעולה: ' + error.message);
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return styles.severityLow || 'text-green-600 bg-green-50 border-green-200';
      case 'medium': return styles.severityMedium || 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'high': return styles.severityHigh || 'text-orange-600 bg-orange-50 border-orange-200';
      case 'critical': return styles.severityCritical || 'text-red-600 bg-red-50 border-red-200';
      default: return styles.severityDefault || 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'low': return '✓';
      case 'medium': return '⚠';
      case 'high': return '⚠';
      case 'critical': return '🗑';
      default: return 'ℹ';
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        {!showConfirmation ? (
          <>
            {/* Main deletion options dialog */}
            <div className={styles.modalHeader}>
              <h3>הסרת משתמש: {user?.FullName}</h3>
              <p>בחר את סוג ההסרה המתאים:</p>
            </div>

            {loading && deletionOptions.length === 0 ? (
              <div className={styles.loadingContainer}>
                <Loader size={24} className={styles.loadingSpinner} />
                <p>טוען אפשרויות...</p>
              </div>
            ) : (
              <div className={styles.optionsContainer}>
                {deletionOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleActionSelect(option)}
                    disabled={option.disabled || loading}
                    className={`${styles.optionButton} ${getSeverityColor(option.severity)} ${
                      option.disabled ? styles.disabled : ''
                    }`}
                  >
                    <div className={styles.optionContent}>
                      <span className={styles.severityIcon}>
                        {getSeverityIcon(option.severity)}
                      </span>
                      <div className={styles.optionText}>
                        <div className={styles.optionLabel}>{option.label}</div>
                        <div className={styles.optionDescription}>{option.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className={styles.modalActions}>
              <button
                onClick={onClose}
                className={styles.cancelButton}
                disabled={loading}
              >
                ביטול
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Confirmation dialog */}
            <div className={styles.modalHeader}>
              <h3 className={styles.confirmationTitle}>
                <AlertTriangle size={24} />
                אישור פעולה
              </h3>
              <p>האם אתה בטוח שברצונך לבצע: <strong>{selectedAction?.label}</strong>?</p>
              <p className={styles.confirmationDescription}>{selectedAction?.description}</p>
              
              {selectedAction?.severity === 'critical' && (
                <div className={styles.confirmationInput}>
                  <label>הקלד "מחק" לאישור:</label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="מחק"
                    className={styles.confirmationTextInput}
                  />
                </div>
              )}
            </div>

            <div className={styles.modalActions}>
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  setConfirmText('');
                }}
                className={styles.cancelButton}
                disabled={loading}
              >
                ביטול
              </button>
              <button
                onClick={() => executeAction(selectedAction)}
                disabled={
                  loading || 
                  (selectedAction?.severity === 'critical' && confirmText !== 'מחק')
                }
                className={styles.confirmButton}
              >
                {loading ? 'מבצע...' : 'אישור'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Action Menu Modal Component (replaces dropdown)
const ActionMenu = ({ user, buildingId, apartmentId, onEdit, onDelete, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Quick actions for common operations
  const handleQuickRemoveFromApartment = async () => {
    try {
      await removeUserFromApartment(user.UserID, buildingId, apartmentId);
      alert('המשתמש הוסר מהדירה בהצלחה');
      window.dispatchEvent(new Event('userDataChanged'));
    } catch (error) {
      alert(`שגיאה: ${error.message}`);
    }
  };

  const handleQuickRemoveManagerRole = async () => {
    try {
      await removeManagerRole(user.UserID, buildingId);
      alert('הרשאות הניהול הוסרו בהצלחה');
      window.dispatchEvent(new Event('userDataChanged'));
    } catch (error) {
      alert(`שגיאה: ${error.message}`);
    }
  };

  const isManager = user.IsManager === "כן";
  const actions = [
    {
      label: '✏️ ערוך',
      onClick: () => onEdit(user),
      disabled: loading
    }
  ];

  // Add context-specific quick actions
  if (apartmentId) {
    actions.push({
      label: '👤 הסר מדירה',
      onClick: handleQuickRemoveFromApartment,
      disabled: loading
    });
  }

  if (isManager) {
    actions.push({
      label: '🛡️ הסר ניהול',
      onClick: handleQuickRemoveManagerRole,
      disabled: loading
    });
  }

  // Add smart deletion option
  actions.push({
    label: '🗑️ אפשרויות הסרה',
    onClick: () => onDelete(user),
    disabled: loading,
    isDanger: true
  });

  const handleActionClick = (action) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
        onClick={() => setIsOpen(true)}
        disabled={loading}
        aria-label="פתח תפריט פעולות"
      >
        <span>⋯</span>
      </button>

      {isOpen && createPortal(
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.actionMenuHeader}>
              <h3>פעולות</h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="סגור"
              >
                ✕
              </button>
            </div>
            <div className={styles.actionMenuContent}>
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Enhanced UserCard Component for Mobile View - Modern Profile Card Design
const UserCard = ({ user, buildingId, onEdit, onDelete, getRoleDisplay, getRoleColor }) => {
  const [showMenu, setShowMenu] = useState(false);

  // Generate initials from name for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get background color based on role
  const getAvatarColor = (user) => {
    if (user.IsManager === "כן") return '#A78BFA'; // Purple
    if (user.IsVaad === "כן") return '#FCD34D'; // Yellow
    if (user.IsTenant === "כן") return '#86EFAC'; // Green
    return '#86EFAC'; // Green default
  };

  // Get role text based on permissions
  const getUserRole = (user) => {
    const roles = [];
    if (user.IsManager === "כן") roles.push('מנהל');
    if (user.IsVaad === "כן") roles.push('חבר ועד');
    if (user.IsTenant === "כן" && roles.length === 0) roles.push('דייר');
    return roles.join(', ') || 'דייר';
  };

  const handleMenuToggle = () => {
    setShowMenu(!showMenu);
  };

  const handleEdit = () => {
    setShowMenu(false);
    onEdit(user);
  };

  const handleDelete = () => {
    setShowMenu(false);
    onDelete(user);
  };

  return (
    <div className={styles.profileCard}>
      {/* Menu Button - Top Right */}
      <div className={styles.menuContainer}>
        <button
          className={styles.menuBtn}
          onClick={handleMenuToggle}
          aria-label="תפריט פעולות"
        >
          <MoreVertical size={20} />
        </button>

        {showMenu && (
          <>
            <div className={styles.menuOverlay} onClick={() => setShowMenu(false)} />
            <div className={styles.menuDropdown}>
              <button className={styles.menuItem} onClick={handleEdit}>
                <Edit size={16} />
                <span>ערוך</span>
              </button>
              <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={handleDelete}>
                <Trash size={16} />
                <span>מחק</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Avatar Section */}
      <div
        className={styles.profileAvatar}
        style={{ backgroundColor: getAvatarColor(user) }}
      >
        <span className={styles.avatarInitials}>{getInitials(user.FullName)}</span>
      </div>

      {/* User Info Section */}
      <div className={styles.profileInfo}>
        <h2 className={styles.profileName}>{user.FullName}</h2>
        <p className={styles.profileUsername}>{user.RelationshipType || 'לא צוין'}</p>
        <p className={styles.profileRole}>דירה {user.ApartmentNumber || '-'}</p>
        <p className={styles.profilePhone} dir="ltr">{user.Phone || '054-0000000'}</p>
      </div>

      {/* Stats Section */}
      <div className={styles.profileStats}>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>דירה</div>
          <div className={styles.statValue}>{user.ApartmentNumber || '-'}</div>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>דמי ועד</div>
          <div className={styles.statValue}>₪{user.VaadMonthly || 0}</div>
        </div>
      </div>
    </div>
  );
};

// Related Tables Modal Component
const RelatedTablesModal = ({ isOpen, onClose, onAddColumn, customColumns = [] }) => {
  const [relatedTables, setRelatedTables] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedTable, setExpandedTable] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (isOpen) {
      loadRelatedTables();
      setExpandedTable(null); // Reset expanded state when modal opens
    }
  }, [isOpen]);

  const loadRelatedTables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRelatedTablesSchema('Users');
      setRelatedTables(response.tables || []);
    } catch (err) {
      console.error('Error loading related tables:', err);
      if (err.message.includes('401')) {
        setError('שגיאת הרשאה. אנא וודא שה-Backend API פועל ושאתה מחובר למערכת.');
      } else if (err.message.includes('Failed to fetch')) {
        setError('לא ניתן להתחבר ל-API. אנא וודא שה-Backend פועל על https://localhost:7204');
      } else {
        setError('שגיאה בטעינת הטבלאות. אנא נסה שנית.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddColumn = (tableName, column) => {
    onAddColumn(tableName, column);
    // Don't close the modal - let user close it manually with the סגור button
  };

  const toggleTable = (index) => {
    setExpandedTable(expandedTable === index ? null : index);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modalContent} ${styles.relatedTablesModal}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>
            <Database size={20} />
            הוסף עמודות מטבלאות קשורות
          </h3>
          <p>בחר עמודות מטבלאות שקשורות למשתמשים</p>
          <button
            onClick={onClose}
            className={styles.userModalCloseButton}
            aria-label="סגור"
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.relatedTablesContent}>
          {loading ? (
            <div className={styles.loadingState}>
              <Loader size={32} className={styles.spinner} />
              <p>טוען טבלאות...</p>
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <AlertTriangle size={32} />
              <p>{error}</p>
              <button onClick={loadRelatedTables} className={styles.retryButton}>
                נסה שנית
              </button>
            </div>
          ) : relatedTables.length === 0 ? (
            <div className={styles.emptyState}>
              <Database size={32} />
              <p>לא נמצאו טבלאות קשורות</p>
            </div>
          ) : (
            <div className={styles.tablesGrid}>
              {relatedTables.map((table, index) => {
                const isExpanded = expandedTable === index;
                return (
                  <div key={index} className={styles.tableCard}>
                    <div
                      className={styles.tableCardHeader}
                      onClick={() => toggleTable(index)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Database size={18} />
                      <h4>{table.displayName}</h4>
                      <span className={styles.tableBadge}>{table.relationshipType}</span>
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                    {isExpanded && (
                      <div className={styles.columnsList}>
                        {(table.columns || []).map((column, colIndex) => {
                          const columnKey = `${table.tableName.toLowerCase()}_${column.name.toLowerCase()}`;
                          const isAdded = customColumns.some(col => col.key === columnKey);

                          return (
                            <div key={colIndex} className={styles.columnItem}>
                              <div className={styles.columnInfo}>
                                <span className={styles.columnName}>{column.displayName}</span>
                                <span className={styles.columnType}>{column.type}</span>
                              </div>
                              <button
                                onClick={() => handleAddColumn(table.tableName, column)}
                                className={styles.addColumnButton}
                                title={isAdded ? 'העמודה כבר נוספה' : `הוסף ${column.displayName}`}
                                disabled={isAdded}
                                style={{ opacity: isAdded ? 0.5 : 1, cursor: isAdded ? 'not-allowed' : 'pointer' }}
                              >
                                <Plus size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.modalActions}>
          <button
            onClick={onClose}
            className={styles.confirmButton}
          >
            סגור
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Column Settings Modal Component
const ColumnSettingsModal = ({ isOpen, onClose, visibleColumns, toggleColumn, resetColumns, isDragEnabled, toggleDragEnabled, onOpenRelatedTables, customColumns, onRemoveCustomColumn }) => {
  if (!isOpen) return null;

  const columnOptions = [
    { key: 'name', label: 'שם', icon: User },
    { key: 'phone', label: 'טלפון', icon: Phone },
    { key: 'floor', label: 'קומה', icon: Building },
    { key: 'apartment', label: 'דירה', icon: Home },
    { key: 'relationshipType', label: 'סוג קשר', icon: UsersIcon },
    { key: 'monthlyAmount', label: 'סכום חודשי', icon: MapPin },
    { key: 'permissions', label: 'הרשאות משתמש', icon: Shield },
    { key: 'actions', label: 'פעולות', icon: Settings }
  ];

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modalContent} ${styles.columnSettingsModal}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>
            <Settings size={20} />
            הגדרות עמודות
          </h3>
          <p>בחר אילו עמודות להציג בטבלה</p>
          <button
            onClick={onClose}
            className={styles.userModalCloseButton}
            aria-label="סגור"
          >
            <X size={20} />
          </button>
        </div>

        {/* Drag and drop toggle */}
        <div className={styles.dragToggleContainer}>
          <label className={styles.dragToggleLabel}>
            <input
              type="checkbox"
              checked={isDragEnabled}
              onChange={toggleDragEnabled}
              className={styles.columnCheckbox}
            />
            <div className={styles.dragToggleContent}>
              <ArrowUpDown size={16} />
              <span>{isDragEnabled ? 'מבוטל' : 'שינוי עמודות'}</span>
              <span className={styles.dragToggleHint}>
                {isDragEnabled ? 'מאופשר - גרור עמודות לשינוי סדר' : 'מבוטל'}
              </span>
            </div>
          </label>
        </div>

        {/* Add Related Tables Button */}
        <div className={styles.addRelatedTablesSection}>
          <button
            onClick={onOpenRelatedTables}
            className={styles.addRelatedTablesButton}
            title="הוסף עמודות מטבלאות קשורות"
          >
            <Database size={18} />
            <span>הוסף עמודות מטבלאות קשורות</span>
            <Plus size={16} />
          </button>
        </div>

        {/* Custom Columns Section */}
        {customColumns && customColumns.length > 0 && (
          <div className={styles.customColumnsSection}>
            <h4 className={styles.sectionTitle}>
              <Database size={16} />
              עמודות מותאמות אישית
            </h4>
            <div className={styles.customColumnsList}>
              {customColumns.map(col => (
                <div key={col.key} className={styles.customColumnItem}>
                  <span className={styles.customColumnLabel}>{col.label}</span>
                  <span className={styles.customColumnSource}>מ{col.tableName}</span>
                  <button
                    onClick={() => onRemoveCustomColumn(col.key)}
                    className={styles.removeCustomColumnButton}
                    title="הסר עמודה מותאמת אישית"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.columnOptionsContainer}>
          {columnOptions.map(({ key, label, icon: Icon }) => (
            <div key={key} className={styles.columnOption}>
              <label className={styles.columnLabel}>
                <input
                  type="checkbox"
                  checked={visibleColumns[key]}
                  onChange={() => toggleColumn(key)}
                  className={styles.columnCheckbox}
                />
                <div className={styles.columnOptionContent}>
                  <Icon size={16} />
                  <span>{label}</span>
                  {visibleColumns[key] ? (
                    <Eye size={14} className={styles.visibilityIcon} />
                  ) : (
                    <EyeOff size={14} className={styles.visibilityIcon} />
                  )}
                </div>
              </label>
            </div>
          ))}
        </div>

        <div className={styles.modalActions}>
          <button
            onClick={resetColumns}
            className={styles.resetButton}
            title="איפוס להגדרות ברירת המחדל"
          >
            <RotateCcw size={16} />
            איפוס
          </button>
          <button
            onClick={onClose}
            className={styles.confirmButton}
          >
            <Settings size={16} />
            סגור
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Summary Modal Component
const SummaryModal = ({ isOpen, columnLabel, data, onClose }) => {
  if (!isOpen) return null;

  const totalOccurrences = data.reduce((sum, item) => sum + item.count, 0);

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.summaryModalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <BarChart3 size={24} />
          <h3>סיכום למופעים - {columnLabel}</h3>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.summaryStats}>
          <div className={styles.summaryStat}>
            <span className={styles.summaryStatLabel}>סה"כ רשומות:</span>
            <span className={styles.summaryStatValue}>{totalOccurrences}</span>
          </div>
          <div className={styles.summaryStat}>
            <span className={styles.summaryStatLabel}>ערכים ייחודיים:</span>
            <span className={styles.summaryStatValue}>{data.length}</span>
          </div>
        </div>

        <div className={styles.summaryTableContainer}>
          <table className={styles.summaryTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>ערך</th>
                <th>מופעים</th>
                <th>אחוז</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td className={styles.summaryValue}>{item.value}</td>
                  <td className={styles.summaryCount}>{item.count}</td>
                  <td className={styles.summaryPercentage}>
                    {((item.count / totalOccurrences) * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.modalActions}>
          <button onClick={onClose} className={styles.confirmButton}>
            סגור
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Sort Menu Component
const SortMenu = ({ isOpen, columnKey, columnLabel, sortKey, position, onClose, onSort, onAddSecondarySort, onClearSort, sortLevels, columnSum, isNumericColumn, onShowSummary }) => {
  if (!isOpen) return null;

  // Find if this column is in the sort levels and at what position
  const currentSortIndex = sortLevels.findIndex(s => s.column === sortKey);
  const isCurrentColumn = currentSortIndex !== -1;
  const currentDirection = isCurrentColumn ? sortLevels[currentSortIndex].direction : null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999
        }}
        onClick={onClose}
      />
      {/* Menu */}
      <div
        className={styles.sortMenu}
        style={{
          position: 'fixed',
          top: `${position.y}px`,
          left: `${position.x}px`,
          zIndex: 1000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.sortMenuHeader}>
          <ArrowUpDown size={16} />
          <span>מיון: {columnLabel}</span>
          <button onClick={onClose} className={styles.sortMenuClose}>
            <X size={16} />
          </button>
        </div>
        <div className={styles.sortMenuOptions}>
          <button
            onClick={() => onSort('asc', false)}
            className={`${styles.sortMenuOption} ${isCurrentColumn && currentDirection === 'asc' && currentSortIndex === 0 ? styles.activeSortOption : ''}`}
          >
            <ChevronUp size={16} />
            מיון עולה (א-ת)
            {isCurrentColumn && currentDirection === 'asc' && currentSortIndex === 0 && <span className={styles.checkMark}>✓</span>}
          </button>
          <button
            onClick={() => onSort('desc', false)}
            className={`${styles.sortMenuOption} ${isCurrentColumn && currentDirection === 'desc' && currentSortIndex === 0 ? styles.activeSortOption : ''}`}
          >
            <ChevronDown size={16} />
            מיון יורד (ת-א)
            {isCurrentColumn && currentDirection === 'desc' && currentSortIndex === 0 && <span className={styles.checkMark}>✓</span>}
          </button>

          <div className={styles.sortMenuDivider}></div>

          <button
            onClick={() => onAddSecondarySort('asc')}
            className={`${styles.sortMenuOption} ${isCurrentColumn && currentDirection === 'asc' && currentSortIndex > 0 ? styles.activeSortOption : ''}`}
          >
            <ChevronUp size={16} />
            <span className={styles.secondarySortLabel}>הוסף מיון משני עולה</span>
            {isCurrentColumn && currentDirection === 'asc' && currentSortIndex > 0 && <span className={styles.sortBadge}>{currentSortIndex + 1}</span>}
          </button>
          <button
            onClick={() => onAddSecondarySort('desc')}
            className={`${styles.sortMenuOption} ${isCurrentColumn && currentDirection === 'desc' && currentSortIndex > 0 ? styles.activeSortOption : ''}`}
          >
            <ChevronDown size={16} />
            <span className={styles.secondarySortLabel}>הוסף מיון משני יורד</span>
            {isCurrentColumn && currentDirection === 'desc' && currentSortIndex > 0 && <span className={styles.sortBadge}>{currentSortIndex + 1}</span>}
          </button>

          {sortLevels.length > 0 && (
            <>
              <div className={styles.sortMenuDivider}></div>
              <button
                onClick={onClearSort}
                className={styles.sortMenuOption}
              >
                <X size={16} />
                נקה את כל המיונים
              </button>
            </>
          )}

          <div className={styles.sortMenuDivider}></div>
          <button
            onClick={() => {
              onShowSummary();
              onClose();
            }}
            className={styles.sortMenuOption}
          >
            <BarChart3 size={16} />
            סיכום למופעים
          </button>

          {isNumericColumn && columnSum !== null && (
            <>
              <div className={styles.sortMenuDivider}></div>
              <div className={styles.columnSumDisplay}>
                <span className={styles.sumLabel}>סה"כ:</span>
                <span className={styles.sumValue}>
                  {sortKey === 'monthlyAmount' ? `₪${columnSum.toLocaleString()}` : columnSum.toLocaleString()}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

// Enhanced Stats Card Component
const StatCard = ({ icon: Icon, title, value, color = "primary" }) => {
  return (
    <div className={styles.statCard}>
      <div className={styles.statIcon} style={{ color: `var(--${color}-color)` }}>
        <Icon size={24} />
      </div>
      <div>
        <h3>{title}</h3>
        <p className={styles.statValue}>{value}</p>
      </div>
    </div>
  );
};

const Users = () => {
  const [residents, setResidents] = useState([]);
  const [filteredResidents, setFilteredResidents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApartment, setSelectedApartment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [residentsPerPage, setResidentsPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('usersTable_visibleColumns');
    return saved ? JSON.parse(saved) : {
      name: true,
      phone: true,
      floor: true,
      apartment: true,
      relationshipType: true,
      monthlyAmount: true,
      permissions: true,
      actions: true
    };
  });

  // Column order state - for drag and drop reordering
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('usersTable_columnOrder');
    return saved ? JSON.parse(saved) : ['name', 'phone', 'floor', 'apartment', 'relationshipType', 'monthlyAmount', 'permissions', 'actions'];
  });

  const [draggedColumn, setDraggedColumn] = useState(null);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showRelatedTablesModal, setShowRelatedTablesModal] = useState(false);

  // Custom columns from related tables
  const [customColumns, setCustomColumns] = useState(() => {
    const saved = localStorage.getItem('usersTable_customColumns');
    return saved ? JSON.parse(saved) : [];
    // Format: [{ key: 'building_address', label: 'כתובת בניין', tableName: 'Building', columnName: 'Address', type: 'string' }]
  });

  // Drag and drop enabled state
  const [isDragEnabled, setIsDragEnabled] = useState(() => {
    const saved = localStorage.getItem('usersTable_dragEnabled');
    return saved ? JSON.parse(saved) : false;
  });

  // Sorting states - Support multiple sort levels
  const [sortLevels, setSortLevels] = useState(() => {
    const saved = localStorage.getItem('usersTable_sortLevels');
    return saved ? JSON.parse(saved) : [{ column: 'apartment', direction: 'asc' }];
  });

  // Sort menu state
  const [sortMenuState, setSortMenuState] = useState({
    isOpen: false,
    columnKey: null,
    position: { x: 0, y: 0 }
  });

  // Summary modal state
  const [summaryModal, setSummaryModal] = useState({
    isOpen: false,
    columnKey: null,
    columnLabel: null,
    data: []
  });

  // Smart deletion modal state
  const [smartDeletionModal, setSmartDeletionModal] = useState({
    isOpen: false,
    user: null
  });

  // User details modal state
  const [userDetailsModal, setUserDetailsModal] = useState({
    isOpen: false,
    user: null
  });

  // VaadMonthly adjustment modal state
  const [vaadMonthlyModal, setVaadMonthlyModal] = useState({
    isOpen: false,
    amount: '',
    isProcessing: false
  });

  // Statistics - only count tenants
  const tenantsOnly = residents.filter(r => r.IsTenant === 'כן' || r.IsTenant === true);
  const totalResidents = tenantsOnly.length;
  const totalCommitteeMembers = tenantsOnly.filter(r => r.IsVaad == 'כן' || r.IsManager == 'כן').length;
  const totalAdmins = tenantsOnly.filter(r => r.IsManager == 'כן').length;
  const totalTenants = tenantsOnly.length;

  // Get unique apartment numbers for dropdown - only from tenants
  const uniqueApartments = React.useMemo(() => {
    const tenantsForApartments = residents.filter(r => r.IsTenant === 'כן' || r.IsTenant === true);
    const apartments = tenantsForApartments
      .map(r => r.ApartmentNumber)
      .filter(apt => apt && apt !== 'לא צוין')
      .map(apt => String(apt))
      .filter((apt, index, arr) => arr.indexOf(apt) === index)
      .sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });
    return apartments;
  }, [residents]);

  // Get building ID helper
  const getBuildingId = () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    return userData?.buildingId;
  };

  // Column visibility functions
  const toggleColumn = (columnKey) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('usersTable_visibleColumns', JSON.stringify(newVisibleColumns));
  };

  const toggleDragEnabled = () => {
    const newValue = !isDragEnabled;
    setIsDragEnabled(newValue);
    localStorage.setItem('usersTable_dragEnabled', JSON.stringify(newValue));
  };

  const resetColumns = () => {
    const confirmMessage = `האם אתה בטוח שברצונך לאפס את ההגדרות?

הפעולה תבצע את השינויים הבאים:
• החזרת כל העמודות למצב ברירת המחדל
• איפוס סדר העמודות
• מחיקת כל העמודות המותאמות אישית שהוספת מטבלאות קשורות

לא ניתן לבטל פעולה זו.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const defaultColumns = {
      name: true,
      phone: true,
      floor: true,
      apartment: true,
      relationshipType: true,
      monthlyAmount: true,
      permissions: true,
      actions: true
    };
    setVisibleColumns(defaultColumns);
    localStorage.setItem('usersTable_visibleColumns', JSON.stringify(defaultColumns));

    // Also reset column order
    const defaultOrder = ['name', 'phone', 'floor', 'apartment', 'relationshipType', 'monthlyAmount', 'permissions', 'actions'];
    setColumnOrder(defaultOrder);
    localStorage.setItem('usersTable_columnOrder', JSON.stringify(defaultOrder));

    // Clear custom columns
    setCustomColumns([]);
    localStorage.removeItem('usersTable_customColumns');
  };

  // Drag and drop handlers for column reordering
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, columnKey) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnKey) {
      const newOrder = [...columnOrder];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(columnKey);

      // Remove dragged column and insert at target position
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedColumn);

      setColumnOrder(newOrder);
    }
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedColumn(null);
    // Save to localStorage
    localStorage.setItem('usersTable_columnOrder', JSON.stringify(columnOrder));
  };

  const handleDrop = (e) => {
    e.preventDefault();
  };

  // Related tables handlers
  const handleAddColumnFromRelatedTable = (tableName, column) => {
    // Create a unique key for the custom column
    const columnKey = `${tableName.toLowerCase()}_${column.name.toLowerCase()}`;

    // Check if column already exists
    if (customColumns.some(col => col.key === columnKey)) {
      return;
    }

    // Create the new custom column
    const newColumn = {
      key: columnKey,
      label: column.displayName,
      tableName: tableName,
      columnName: column.name,
      type: column.type,
      sortable: true,
      sortKey: columnKey
    };

    // Add to custom columns
    const updatedCustomColumns = [...customColumns, newColumn];
    setCustomColumns(updatedCustomColumns);
    localStorage.setItem('usersTable_customColumns', JSON.stringify(updatedCustomColumns));

    // Add to visible columns
    const updatedVisibleColumns = { ...visibleColumns, [columnKey]: true };
    setVisibleColumns(updatedVisibleColumns);
    localStorage.setItem('usersTable_visibleColumns', JSON.stringify(updatedVisibleColumns));

    // Add to column order (before 'actions' column)
    const actionsIndex = columnOrder.indexOf('actions');
    const newColumnOrder = [...columnOrder];
    newColumnOrder.splice(actionsIndex, 0, columnKey);
    setColumnOrder(newColumnOrder);
    localStorage.setItem('usersTable_columnOrder', JSON.stringify(newColumnOrder));

    // Refresh the data to include the new column
    loadUsers();
  };

  const handleOpenRelatedTables = () => {
    setShowColumnSettings(false);
    setShowRelatedTablesModal(true);
  };

  const handleRemoveCustomColumn = (columnKey) => {
    // Remove from customColumns
    const updated = customColumns.filter(col => col.key !== columnKey);
    setCustomColumns(updated);
    localStorage.setItem('usersTable_customColumns', JSON.stringify(updated));

    // Remove from columnOrder
    const newOrder = columnOrder.filter(key => key !== columnKey);
    setColumnOrder(newOrder);
    localStorage.setItem('usersTable_columnOrder', JSON.stringify(newOrder));

    // Remove from visibleColumns
    const newVisible = { ...visibleColumns };
    delete newVisible[columnKey];
    setVisibleColumns(newVisible);
    localStorage.setItem('usersTable_visibleColumns', JSON.stringify(newVisible));
  };

  // Sort menu handlers
  const handleColumnHeaderClick = (e, columnKey, isSortable) => {
    if (!isSortable) return;

    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setSortMenuState({
      isOpen: true,
      columnKey: columnKey,
      position: {
        x: rect.left,
        y: rect.bottom
      }
    });
  };

  const closeSortMenu = () => {
    setSortMenuState({
      isOpen: false,
      columnKey: null,
      position: { x: 0, y: 0 }
    });
  };

  // Calculate sum for numeric columns
  const calculateColumnSum = (columnKey) => {
    const column = columnDefinitions[columnKey];
    if (!column) return null;

    const sortKey = column.sortKey;

    // Define which columns are numeric
    const numericColumns = ['monthlyAmount', 'floor', 'apartment'];
    if (!numericColumns.includes(sortKey)) return null;

    // Calculate sum from filtered residents (what user sees)
    let sum = 0;
    filteredResidents.forEach(resident => {
      let value = 0;
      switch (sortKey) {
        case 'monthlyAmount':
          value = parseFloat(resident.VaadMonthly) || 0;
          break;
        case 'floor':
          value = parseFloat(resident.FloorNumber) || 0;
          break;
        case 'apartment':
          value = parseFloat(resident.ApartmentNumber) || 0;
          break;
        default:
          value = 0;
      }
      sum += value;
    });

    return sum;
  };

  const calculateColumnSummary = (columnKey) => {
    if (!columnKey || !columnDefinitions[columnKey]) return [];

    const column = columnDefinitions[columnKey];
    const sortKey = column.sortKey;

    // Count occurrences of each unique value
    const occurrences = {};

    filteredResidents.forEach(resident => {
      let value = null;

      // Extract value based on the column
      if (customColumns.some(col => col.key === columnKey)) {
        // Custom column
        value = resident[columnKey];
      } else {
        // Standard column
        switch (sortKey) {
          case 'name':
            value = resident.Name;
            break;
          case 'apartment':
            value = resident.ApartmentNumber;
            break;
          case 'floor':
            value = resident.FloorNumber;
            break;
          case 'phone':
            value = resident.PhoneNumber;
            break;
          case 'email':
            value = resident.Email;
            break;
          case 'relationshipType':
            value = resident.RelationshipType;
            break;
          case 'role':
            value = resident.IsTenant === 'כן' || resident.IsTenant === true ? 'דייר' :
                    resident.IsVaad === 'כן' || resident.IsVaad === true ? 'ועד' :
                    resident.IsManager === 'כן' || resident.IsManager === true ? 'מנהל' : 'אחר';
            break;
          case 'monthlyAmount':
            value = resident.VaadMonthly;
            break;
          default:
            value = resident[sortKey];
        }
      }

      // Convert to string for grouping and normalize
      let valueStr;
      if (value === null || value === undefined || value === '') {
        valueStr = 'לא זמין';
      } else {
        // Convert to string and trim whitespace
        valueStr = String(value).trim();
        // If empty after trim, mark as not available
        if (valueStr === '') {
          valueStr = 'לא זמין';
        }
      }

      if (occurrences[valueStr]) {
        occurrences[valueStr]++;
      } else {
        occurrences[valueStr] = 1;
      }
    });

    // Convert to array and sort by count (descending)
    return Object.entries(occurrences)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
  };

  const handleShowSummary = () => {
    if (sortMenuState.columnKey) {
      const summaryData = calculateColumnSummary(sortMenuState.columnKey);
      setSummaryModal({
        isOpen: true,
        columnKey: sortMenuState.columnKey,
        columnLabel: columnDefinitions[sortMenuState.columnKey]?.label || '',
        data: summaryData
      });
    }
  };

  const handleSortSelection = (direction, addAsSecondary = false) => {
    if (sortMenuState.columnKey) {
      const column = columnDefinitions[sortMenuState.columnKey];
      if (column && column.sortable) {
        let newSortLevels;

        if (addAsSecondary) {
          // Add as secondary sort (append to existing)
          // Remove if already exists first
          newSortLevels = sortLevels.filter(s => s.column !== column.sortKey);
          // Add to the end
          newSortLevels.push({ column: column.sortKey, direction });
        } else {
          // Set as primary sort (replace all)
          newSortLevels = [{ column: column.sortKey, direction }];
        }

        setSortLevels(newSortLevels);
        localStorage.setItem('usersTable_sortLevels', JSON.stringify(newSortLevels));
      }
    }
    closeSortMenu();
  };

  const handleAddSecondarySort = (direction) => {
    handleSortSelection(direction, true);
  };

  const handleClearSort = () => {
    const defaultSort = [{ column: 'apartment', direction: 'asc' }];
    setSortLevels(defaultSort);
    localStorage.setItem('usersTable_sortLevels', JSON.stringify(defaultSort));
    closeSortMenu();
  };

  // Column definitions with labels and sortability
  // Merge base columns with custom columns
  const columnDefinitions = React.useMemo(() => {
    const baseColumns = {
      name: { label: 'שם', sortable: true, sortKey: 'name' },
      phone: { label: 'טלפון', sortable: true, sortKey: 'phone' },
      floor: { label: 'קומה', sortable: true, sortKey: 'floor' },
      apartment: { label: 'דירה', sortable: true, sortKey: 'apartment' },
      relationshipType: { label: 'סוג קשר', sortable: true, sortKey: 'relationshipType' },
      monthlyAmount: { label: 'סכום חודשי', sortable: true, sortKey: 'monthlyAmount' },
      permissions: { label: 'הרשאות משתמש', sortable: false },
      actions: { label: 'פעולות', sortable: false }
    };

    // Add custom columns from related tables
    customColumns.forEach(col => {
      baseColumns[col.key] = {
        label: col.label,
        sortable: col.sortable,
        sortKey: col.sortKey,
        isCustom: true,
        tableName: col.tableName,
        columnName: col.columnName
      };
    });

    return baseColumns;
  }, [customColumns]);

  // Helper function to get sort value for a single column
  const getSortValue = (item, column) => {
    switch (column) {
      case 'apartment':
        return item.ApartmentNumber;
      case 'floor':
        return item.FloorNumber;
      case 'name':
        return (item.FullName || '').toLowerCase();
      case 'email':
        return (item.Email || '').toLowerCase();
      case 'phone':
        return (item.Phone || '').toLowerCase();
      case 'relationshipType':
        return (item.RelationshipType || '').toLowerCase();
      case 'monthlyAmount':
        return parseFloat(item.VaadMonthly) || 0;
      default:
        return '';
    }
  };

  // Compare function for a single column
  const compareValues = (a, b, column, direction) => {
    let valueA = getSortValue(a, column);
    let valueB = getSortValue(b, column);

    // Handle apartment numbers specially
    if (column === 'apartment') {
      if (!valueA || valueA === 'לא צוין') valueA = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      if (!valueB || valueB === 'לא צוין') valueB = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;

      const numA = parseInt(valueA);
      const numB = parseInt(valueB);

      if (!isNaN(numA) && !isNaN(numB)) {
        return direction === 'asc' ? numA - numB : numB - numA;
      } else if (!isNaN(numA)) {
        return direction === 'asc' ? -1 : 1;
      } else if (!isNaN(numB)) {
        return direction === 'asc' ? 1 : -1;
      } else {
        const result = String(valueA).localeCompare(String(valueB));
        return direction === 'asc' ? result : -result;
      }
    }

    // Handle floor numbers specially
    if (column === 'floor') {
      if (!valueA || valueA === 'לא צוין') valueA = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      if (!valueB || valueB === 'לא צוין') valueB = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;

      const floorA = parseInt(valueA);
      const floorB = parseInt(valueB);

      if (!isNaN(floorA) && !isNaN(floorB)) {
        return direction === 'asc' ? floorA - floorB : floorB - floorA;
      }
    }

    // Handle monthly amount (numeric)
    if (column === 'monthlyAmount') {
      return direction === 'asc' ? valueA - valueB : valueB - valueA;
    }

    // Handle strings
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      const result = valueA.localeCompare(valueB);
      return direction === 'asc' ? result : -result;
    }

    // Default comparison
    if (valueA < valueB) return direction === 'asc' ? -1 : 1;
    if (valueA > valueB) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  // Multi-level sorting function
  const sortDataMultiLevel = (data, sortLevels) => {
    if (!sortLevels || sortLevels.length === 0) return data;

    return [...data].sort((a, b) => {
      // Try each sort level until we find a difference
      for (let i = 0; i < sortLevels.length; i++) {
        const { column, direction } = sortLevels[i];
        const result = compareValues(a, b, column, direction);
        if (result !== 0) return result;
      }
      return 0;
    });
  };

  // Sorting function (kept for backward compatibility)
  const sortData = (data, column, direction) => {
    if (!column) return data;

    return [...data].sort((a, b) => {
      let valueA, valueB;

      switch (column) {
        case 'apartment':
          valueA = a.ApartmentNumber;
          valueB = b.ApartmentNumber;

          if (!valueA || valueA === 'לא צוין') valueA = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
          if (!valueB || valueB === 'לא צוין') valueB = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;

          const numA = parseInt(valueA);
          const numB = parseInt(valueB);

          if (!isNaN(numA) && !isNaN(numB)) {
            return direction === 'asc' ? numA - numB : numB - numA;
          } else if (!isNaN(numA)) {
            return direction === 'asc' ? -1 : 1;
          } else if (!isNaN(numB)) {
            return direction === 'asc' ? 1 : -1;
          } else {
            const result = String(valueA).localeCompare(String(valueB));
            return direction === 'asc' ? result : -result;
          }

        case 'floor':
          valueA = a.FloorNumber;
          valueB = b.FloorNumber;

          if (!valueA || valueA === 'לא צוין') valueA = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
          if (!valueB || valueB === 'לא צוין') valueB = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;

          const floorA = parseInt(valueA);
          const floorB = parseInt(valueB);

          if (!isNaN(floorA) && !isNaN(floorB)) {
            return direction === 'asc' ? floorA - floorB : floorB - floorA;
          }
          break;

        case 'name':
          valueA = (a.FullName || '').toLowerCase();
          valueB = (b.FullName || '').toLowerCase();
          break;

        case 'email':
          valueA = (a.Email || '').toLowerCase();
          valueB = (b.Email || '').toLowerCase();
          break;

        case 'phone':
          valueA = (a.Phone || '').toLowerCase();
          valueB = (b.Phone || '').toLowerCase();
          break;

        case 'relationshipType':
          valueA = (a.RelationshipType || '').toLowerCase();
          valueB = (b.RelationshipType || '').toLowerCase();
          break;

        case 'monthlyAmount':
          valueA = parseFloat(a.VaadMonthly) || 0;
          valueB = parseFloat(b.VaadMonthly) || 0;
          return direction === 'asc' ? valueA - valueB : valueB - valueA;

        default:
          return 0;
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const result = valueA.localeCompare(valueB);
        return direction === 'asc' ? result : -result;
      }

      if (valueA < valueB) return direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Get sort icon for column header
  const getSortIcon = (column) => {
    // Find if this column is in the sort levels
    const sortIndex = sortLevels.findIndex(s => s.column === column);

    if (sortIndex === -1) {
      return <ArrowUpDown size={14} className={styles.sortIcon} />;
    }

    const { direction } = sortLevels[sortIndex];
    const icon = direction === 'asc'
      ? <ChevronUp size={14} className={styles.sortIconActive} />
      : <ChevronDown size={14} className={styles.sortIconActive} />;

    // Show sort level badge if it's a secondary sort
    if (sortIndex > 0) {
      return (
        <span className={styles.sortWithBadge}>
          {icon}
          <span className={styles.sortLevelBadge}>{sortIndex + 1}</span>
        </span>
      );
    }

    return icon;
  };

  // Load users data
  useEffect(() => {
    loadUsers();
  }, []);

  // Add event listener for building changes
  useEffect(() => {
    const handleBuildingChange = () => {
      loadUsers();
    };

    window.addEventListener('buildingChanged', handleBuildingChange);
    window.addEventListener('userDataChanged', handleBuildingChange);

    return () => {
      window.removeEventListener('buildingChanged', handleBuildingChange);
      window.removeEventListener('userDataChanged', handleBuildingChange);
    };
  }, []);

  // Reload users when custom columns change
  useEffect(() => {
    if (residents.length > 0) {
      loadUsers();
    }
  }, [customColumns.length]);

  // Improved outside click handler for UsersManagement
  useEffect(() => {
    const handleClickOutside = (event) => {
      const actionButtonGroup = document.querySelector(`.${styles.actionButtonGroup}`);
      if (actionButtonGroup && !actionButtonGroup.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('touch', handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touch', handleClickOutside);
    };
  }, [showDropdown, styles.actionButtonGroup]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('user'));
      const buildingId = userData?.buildingId;

      if (!buildingId) {
        setResidents([]);
        return;
      }

      // Use the custom columns endpoint if there are custom columns, otherwise use the regular endpoint
      const data = customColumns.length > 0
        ? await getUsersWithCustomColumns(buildingId)
        : await getListUsers(buildingId);

      console.log('📊 Fetched user data:', data);
      console.log('🔧 Custom columns:', customColumns);

      const transformedData = data.map(user => {
        console.log('👤 Raw user data:', user);

        // Start with base fields
        const transformed = {
          UserID: user.userID || user.UserID,
          FullName: user.fullName || user.FullName,
          Email: user.email || user.Email,
          Phone: user.phone || user.Phone,
          PasswordHash: user.passwordHash || user.PasswordHash || '',
          IsVaad: user.isVaad || user.IsVaad || false,
          IsTenant: user.isTenant || user.IsTenant || false,
          IsManager: user.isManager || user.IsManager || false,
          RelationshipType: user.relationshipType || user.RelationshipType || 'שוכר',
          OwnerName: user.ownerName || user.OwnerName || '',
          OwnerPhone: user.ownerPhone || user.OwnerPhone || '',
          TenantName: user.tenantName || user.TenantName || '',
          TenantPhone: user.tenantPhone || user.TenantPhone || '',
          ApartmentNumber: user.apartmentNumber || user.ApartmentNumber || 'לא צוין',
          FloorNumber: user.floorNumber || user.FloorNumber || 'לא צוין',
          VaadMonthly: user.vaadMonthly || user.VaadMonthly || 0,
          ApartmentID: user.apartmentID || user.ApartmentID || null,
          BuildingId: user.buildingID || user.BuildingID || buildingId,
          City: user.city || user.City || 'תל אביב',
          ParkingNumber: user.parkingNumber || user.ParkingNumber || '',
          ChargingStation: user.chargingStation || user.ChargingStation || false
        };

        // Add custom column fields if they exist
        customColumns.forEach(col => {
          const key = col.key;
          if (user[key] !== undefined) {
            transformed[key] = user[key];
            console.log(`✅ Added custom column ${key}:`, user[key]);
          } else {
            console.log(`❌ Custom column ${key} not found in user data`);
          }
        });

        console.log('🎯 Transformed user:', transformed);
        return transformed;
      });

      // Debug: Check for duplicate emails in the transformed data
      const emailCount = {};
      transformedData.forEach(user => {
        if (user.Email) {
          emailCount[user.Email] = (emailCount[user.Email] || 0) + 1;
        }
      });
      
      const duplicateEmails = Object.entries(emailCount).filter(([email, count]) => count > 1);
      if (duplicateEmails.length > 0) {
        console.warn('⚠️ Duplicate emails found in users data:', duplicateEmails);
      }
      
      
      // CRITICAL DEBUG: Check if all users have the same email (your logged-in email)
      const loggedInUser = JSON.parse(localStorage.getItem('user') || '{}');
      const loggedInEmail = loggedInUser.email;
      const usersWithLoggedInEmail = transformedData.filter(u => u.Email === loggedInEmail);
      
      
      if (usersWithLoggedInEmail.length === transformedData.length && transformedData.length > 1) {
        console.error('❌ CRITICAL BUG: ALL USERS HAVE THE SAME EMAIL AS THE LOGGED-IN USER!');
        console.error('This is likely an API issue where all users are returning your email');
      }

      setResidents(transformedData);

      setSearchTerm('');
      setSelectedApartment('');
      setCurrentPage(1);

    } catch (error) {
      setResidents([]);
      alert('שגיאה בטעינת המשתמשים: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort users
  useEffect(() => {
    let filtered = residents;

    // Always filter to show only tenants by default
    filtered = filtered.filter(r => r.IsTenant === 'כן' || r.IsTenant === true);

    if (selectedApartment) {
      filtered = filtered.filter(resident =>
        String(resident.ApartmentNumber) === selectedApartment
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(resident => {
        const searchLower = searchTerm.toLowerCase();
        const apartmentNumber = resident.ApartmentNumber ?
          String(resident.ApartmentNumber).toLowerCase() : '';
        const floorNumber = resident.FloorNumber ?
          String(resident.FloorNumber).toLowerCase() : '';

        return (
          resident.FullName?.toLowerCase().includes(searchLower) ||
          resident.Email?.toLowerCase().includes(searchLower) ||
          resident.Phone?.includes(searchTerm) ||
          apartmentNumber.includes(searchLower) ||
          floorNumber.includes(searchLower) ||
          resident.RelationshipType?.toLowerCase().includes(searchLower)
        );
      });
    }

    filtered = sortDataMultiLevel(filtered, sortLevels);

    setFilteredResidents(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedApartment, residents, sortLevels]);

  // Clear filters function
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedApartment('');
    const defaultSort = [{ column: 'apartment', direction: 'asc' }];
    setSortLevels(defaultSort);
    localStorage.setItem('usersTable_sortLevels', JSON.stringify(defaultSort));
  };

  // Pagination
  const indexOfLastResident = currentPage * residentsPerPage;
  const indexOfFirstResident = indexOfLastResident - residentsPerPage;
  const currentResidents = filteredResidents.slice(indexOfFirstResident, indexOfLastResident);
  const totalPages = Math.ceil(filteredResidents.length / residentsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowForm(true);
    setShowDropdown(false);
  };

  const handleEditUser = (user) => {
    console.log('🎯 handleEditUser called with user:', {
      UserID: user.UserID,
      FullName: user.FullName,
      Email: user.Email,
      Phone: user.Phone,
      allFields: Object.keys(user),
      entireUser: user
    });
    
    setEditingUser(user);
    setShowForm(true);
    console.log('✅ editingUser state set, showForm=true');
  };

  // Enhanced delete user handler using smart deletion
  const handleDeleteUser = (user) => {
    setSmartDeletionModal({
      isOpen: true,
      user: user
    });
  };

  // Handle row click to show user details
  const handleRowClick = (user) => {
    setUserDetailsModal({
      isOpen: true,
      user: user
    });
  };

  // Handle smart deletion success
  const handleSmartDeletionSuccess = async (result, action) => {
    console.log('Smart deletion successful:', result, action);
    
    // Show success message based on action and result
    let message = result.message || 'הפעולה בוצעה בהצלחה';
    
    // Enhanced messages based on action type and result
    switch (action.action) {
      case 'removeFromApartment':
        if (result.stillInBuilding) {
          const roleText = result.isManager && result.isVaad ? 'מנהל וחבר ועד' :
                          result.isManager ? 'מנהל' : 
                          result.isVaad ? 'חבר ועד' : '';
          message = `המשתמש הוסר מהדירה אך נשאר בבניין${roleText ? ` כ${roleText}` : ''}`;
        } else if (result.removedFromBuilding) {
          message = 'המשתמש הוסר מהדירה ומהבניין לגמרי';
        } else {
          message = result.message || 'המשתמש הוסר מהדירה בהצלחה';
        }
        break;
        
      case 'removeManagerRole':
        if (result.hasApartments) {
          message = 'הרשאות הניהול הוסרו, המשתמש נשאר כדייר';
        } else if (result.newRoles?.isVaad === 'כן') {
          message = 'הרשאות הניהול הוסרו, המשתמש נשאר כחבר ועד';
        } else {
          message = result.message || 'הרשאות הניהול הוסרו בהצלחה';
        }
        break;
        
      case 'removeFromBuilding':
        message = result.message || 'המשתמש הוסר מהבניין בהצלחה';
        break;
        
      case 'deleteCompletely':
        message = 'המשתמש נמחק לחלוטין מהמערכת';
        break;
        
      case 'softDelete':
        message = 'המשתמש הוסר עם שמירת היסטוריה';
        break;
        
      default:
        message = result.message || 'הפעולה בוצעה בהצלחה';
    }
    
    // Show detailed status for complex operations
    if (result.isTenant !== undefined || result.stillInBuilding !== undefined) {
      const statusDetails = [];
      if (result.isTenant === 'לא' && result.stillInBuilding) {
        statusDetails.push('אינו דייר יותר');
      }
      if (result.newRoles) {
        const roles = [];
        if (result.newRoles.isManager === 'כן') roles.push('מנהל');
        if (result.newRoles.isVaad === 'כן') roles.push('חבר ועד');
        if (result.newRoles.isTenant === 'כן') roles.push('דייר');
        if (roles.length > 0) {
          statusDetails.push(`תפקידים נוכחיים: ${roles.join(', ')}`);
        }
      }
      
      if (statusDetails.length > 0) {
        message += `\n(${statusDetails.join(', ')})`;
      }
    }
    
    alert(message);
    
    // Reload users data
    await loadUsers();
  };

  const handleUserSaved = async (savedUser) => {
    try {
      const action = editingUser ? 'עודכן' : 'נוצר';

      setShowForm(false);
      setEditingUser(null);

      await loadUsers();

      alert(`דייר ${action} בהצלחה`);

    } catch (error) {
      setShowForm(false);
      setEditingUser(null);
      alert('הדייר נשמר בהצלחה, אך יש בעיה ברענון הרשימה. אנא רענן את הדף.');
    }
  };

  const handleFileUploadComplete = () => {
    setShowFileUpload(false);
    setShowDropdown(false);
    loadUsers();
  };

  const handleExcelUpload = () => {
    setShowFileUpload(true);
    setShowDropdown(false);
  };

  // Handle VaadMonthly adjustment for all users in building
  const handleVaadMonthlyAdjustment = async (operation) => {
    const amount = parseFloat(vaadMonthlyModal.amount);

    if (isNaN(amount) || amount <= 0) {
      alert('אנא הזן סכום תקין');
      return;
    }

    const buildingId = getBuildingId();
    if (!buildingId) {
      alert('לא נמצא מזהה בניין');
      return;
    }

    const operationText = operation === 'add' ? 'להוסיף' : 'להסיר';
    const confirmMessage = `האם אתה בטוח שברצונך ${operationText} ₪${amount} לכל המשתמשים בבניין?\nפעולה זו תשפיע על ${tenantsOnly.length} משתמשים.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setVaadMonthlyModal(prev => ({ ...prev, isProcessing: true }));

    try {
      const result = await updateVaadMonthlyForBuilding(buildingId, amount, operation);

      // Close modal and refresh users
      setVaadMonthlyModal({ isOpen: false, amount: '', isProcessing: false });
      await loadUsers();

      const successText = operation === 'add' ? 'נוסף' : 'הוסר';
      alert(`${successText} ₪${amount} בהצלחה ל-${result.updatedCount || tenantsOnly.length} משתמשים`);
    } catch (error) {
      console.error('VaadMonthly adjustment error:', error);
      alert(`שגיאה: ${error.message}`);
      setVaadMonthlyModal(prev => ({ ...prev, isProcessing: false }));
    }
  };

  // Excel Export Function (keeping existing implementation)
  const exportToExcel = async (exportAllPages = false) => {
    try {
      setIsExporting(true);
      
      const XLSX = await import('xlsx');
      
      const dataToExport = exportAllPages ? filteredResidents : currentResidents;
      
      if (dataToExport.length === 0) {
        alert('אין נתונים לייצוא');
        return;
      }

      const excelData = dataToExport.map((user, index) => ({
        'מספר רשומה': index + 1,
        'שם מלא': user.FullName || '',
        'דוא"ל': user.Email || '',
        'טלפון': user.Phone || '',
        'מספר דירה': user.ApartmentNumber || '',
        'סוג קשר': user.RelationshipType || '',
        'שם בעלים': user.RelationshipType === 'שוכר' ? (user.OwnerName || '') : '',
        'טלפון בעלים': user.RelationshipType === 'שוכר' ? (user.OwnerPhone || '') : '',
        'מספר קומה': user.FloorNumber || '',
        'מנהל': user.IsManager === 'כן' ? 'כן' : 'לא',
        'חבר ועד': user.IsVaad === 'כן' ? 'כן' : 'לא',
        'דייר': user.IsTenant === 'כן' ? 'כן' : 'לא',
        'וועד חודשי': user.VaadMonthly || 0,
        'מזהה משתמש': user.UserID || '',
        'מזהה בניין': user.BuildingID || '',
        'מזהה דירה': user.ApartmentID || ''
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'משתמשים');

      const colWidths = Object.keys(excelData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      ws['!cols'] = colWidths;

      const currentDate = new Date().toLocaleDateString('he-IL');
      const filename = exportAllPages 
        ? `משתמשים_כל_הנתונים_${currentDate.replace(/\//g, '-')}.xlsx`
        : `משתמשים_עמוד_${currentPage}_${currentDate.replace(/\//g, '-')}.xlsx`;

      XLSX.writeFile(wb, filename);

      const message = exportAllPages 
        ? `ייצוא הושלם בהצלחה! ${dataToExport.length} משתמשים יוצאו לקובץ Excel.`
        : `ייצוא הושלם בהצלחה! ${dataToExport.length} משתמשים מעמוד ${currentPage} יוצאו לקובץ Excel.`;
      
      alert(message);

    } catch (error) {
      alert('שגיאה בייצוא לאקסל. אנא נסה שוב.');
    } finally {
      setIsExporting(false);
    }
  };

  const getRoleDisplay = (user) => {
    const roles = [];
    if (user.IsManager === "כן") roles.push('מנהל');
    if (user.IsVaad === "כן") roles.push('חבר ועד');
    if (user.IsTenant === "כן") roles.push('דייר');

    return roles.length > 0 ? roles.join(', ') : 'לא מוגדר';
  };

  const handleDropdownToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const getRoleColor = (user) => {
    if (user.IsManager) return styles.roleAdmin;
    if (user.IsVaad) return styles.roleCommittee;
    if (user.IsTenant) return styles.roleResident;
    return styles.roleResident;
  };

  // Show UserForm if form is active
  if (showForm) {
    return (
      <UserForm
        user={editingUser}
        onSave={handleUserSaved}
        onCancel={() => setShowForm(false)}
      />
    );
  }

  return (
    <>
      <div className={styles.usersManagement}>
        <div className={styles.header}>
          <div>
            <h1>ניהול דיירים</h1>
          </div>
          <div className={styles.stats}>
            <StatCard
              icon={UsersIcon}
              title="סה״כ דיירים"
              value={totalResidents}
              color="primary"
            />
            <StatCard
              icon={Shield}
              title="חברי ועד"
              value={totalCommitteeMembers}
              color="secondary"
            />
            <StatCard
              icon={Settings}
              title="מנהלים"
              value={totalAdmins}
              color="danger"
            />
          </div>
        </div>

      <div className={styles.actions}>
        <div className={styles.filtersContainer}>
          {isDragEnabled && (
            <button
              onClick={toggleDragEnabled}
              className={styles.confirmDragButton}
              title="אישור שינויים"
            >
              <Check size={18} />
              <span>שמור</span>
            </button>
          )}

          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="חיפוש דיירים..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="חיפוש דיירים"
            />
            <Search size={20} className={styles.searchIcon} />
          </div>

          <div className={styles.apartmentFilter}>
            <input
              type="text"
              value={selectedApartment}
              onChange={(e) => setSelectedApartment(e.target.value)}
              className={styles.apartmentInput}
              placeholder="הזן מספר דירה"
              aria-label="חיפוש מספר דירה"
            />
            <Home size={18} className={styles.filterIcon} />
          </div>

          {(searchTerm || selectedApartment || sortLevels.length > 1 || sortLevels[0]?.column !== 'apartment') && (
            <button
              className={styles.clearFiltersButton}
              onClick={clearFilters}
              title="נקה מסננים"
              aria-label="נקה כל המסננים"
            >
              <Filter size={18} />
              נקה
            </button>
          )}
        </div>

        <div className={styles.buttonsGroup}>
          <button
            className={styles.columnSettingsButton}
            onClick={() => setShowColumnSettings(true)}
            title="הגדרות עמודות"
            aria-label="הגדרות עמודות"
          >
            <Settings size={18} />
            עמודות
          </button>
          
          <div className={styles.actionButtonGroup}>
            <button
              className={styles.addButton}
              onClick={handleAddUser}
              aria-label="הוסף דייר חדש"
            >
              <UserPlus size={20} />
              הוסף דייר חדש
            </button>

            <button
              className={styles.dropdownToggle}
              onClick={handleDropdownToggle}
              onTouchEnd={handleDropdownToggle}
              title="אפשרויות נוספות"
              aria-label="אפשרויות נוספות"
              aria-expanded={showDropdown}
              type="button"
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader size={16} className={styles.loadingIcon} />
              ) : (
                <ChevronDown
                  size={16}
                  style={{
                  transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              />
              )}
            </button>

            {showDropdown && (
              <div className={styles.dropdownSecond} role="menu">
                <button
                  className={styles.dropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    exportToExcel(false);
                    setShowDropdown(false);
                  }}
                  disabled={isExporting || currentResidents.length === 0}
                  role="menuitem"
                  type="button"
                >
                  <FileSpreadsheet size={16} />
                  ייצא עמוד נוכחי לאקסל
                </button>
                
                <button
                  className={styles.dropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    exportToExcel(true);
                    setShowDropdown(false);
                  }}
                  disabled={isExporting || filteredResidents.length === 0}
                  role="menuitem"
                  type="button"
                >
                  <FileSpreadsheet size={16} />
                  ייצא את כל הנתונים לאקסל
                </button>
                
                <div className={styles.dropdownDivider}></div>
                
                <button
                  className={styles.dropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExcelUpload();
                  }}
                  role="menuitem"
                  type="button"
                >
                  <FileSpreadsheet size={16} />
                  העלאה מקובץ Excel/CSV
                </button>

                <div className={styles.dropdownDivider}></div>

                <button
                  className={styles.dropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setVaadMonthlyModal({ isOpen: true, amount: '', isProcessing: false });
                    setShowDropdown(false);
                  }}
                  role="menuitem"
                  type="button"
                >
                  <BarChart3 size={16} />
                  שינוי סכום ועד בית
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingContainer} role="status" aria-live="polite">
          <div className={styles.loadingSpinner} aria-hidden="true"></div>
          <p>טוען משתמשים...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className={styles.tableContainer}>
            <table className={styles.usersTable} role="table">
              <thead>
                <tr role="row">
                  {columnOrder.map(columnKey => {
                    if (!visibleColumns[columnKey]) return null;

                    const column = columnDefinitions[columnKey];
                    const isSortable = column.sortable;
                    const isDragging = draggedColumn === columnKey;

                    return (
                      <th
                        key={columnKey}
                        className={`${isSortable ? styles.sortableHeader : ''} ${isDragging ? styles.dragging : ''}`}
                        onClick={isSortable ? (e) => handleColumnHeaderClick(e, columnKey, isSortable) : undefined}
                        title={
                          isSortable && isDragEnabled
                            ? `לחץ לאפשרויות מיון. גרור להזזת עמודה`
                            : isSortable
                            ? `לחץ לאפשרויות מיון`
                            : isDragEnabled
                            ? 'גרור להזזת עמודה'
                            : ''
                        }
                        role="columnheader"
                        aria-sort={
                          isSortable && sortLevels[0]?.column === column.sortKey
                            ? (sortLevels[0]?.direction === 'asc' ? 'ascending' : 'descending')
                            : 'none'
                        }
                        tabIndex="0"
                        draggable={isDragEnabled}
                        onDragStart={isDragEnabled ? (e) => handleDragStart(e, columnKey) : undefined}
                        onDragOver={isDragEnabled ? handleDragOver : undefined}
                        onDragEnter={isDragEnabled ? (e) => handleDragEnter(e, columnKey) : undefined}
                        onDragEnd={isDragEnabled ? handleDragEnd : undefined}
                        onDrop={isDragEnabled ? handleDrop : undefined}
                        style={{ cursor: isDragging ? 'grabbing' : (isDragEnabled ? 'grab' : (isSortable ? 'pointer' : 'default')) }}
                      >
                        {column.label} {isSortable && getSortIcon(column.sortKey)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {currentResidents.length > 0 ? (
                  currentResidents.map((user, index) => {
                    const renderCell = (columnKey) => {
                      switch (columnKey) {
                        case 'name':
                          return <td key={columnKey} className={styles.userName}>{user.FullName}</td>;
                        case 'phone':
                          return <td key={columnKey}>{user.Phone}</td>;
                        case 'floor':
                          return <td key={columnKey}>{user.FloorNumber || 'לא צוין'}</td>;
                        case 'apartment':
                          return <td key={columnKey}>{user.ApartmentNumber || 'לא צוין'}</td>;
                        case 'relationshipType':
                          return <td key={columnKey}>{user.RelationshipType || 'לא צוין'}</td>;
                        case 'monthlyAmount':
                          return <td key={columnKey}>₪{user.VaadMonthly || 0}</td>;
                        case 'permissions':
                          return (
                            <td key={columnKey}>
                              <span className={`${styles.roleBadge} ${getRoleColor(user)}`}>
                                {getRoleDisplay(user)}
                              </span>
                            </td>
                          );
                        case 'actions':
                          return (
                            <td key={columnKey} className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
                              <ActionMenu
                                user={user}
                                buildingId={getBuildingId()}
                                apartmentId={user.ApartmentID}
                                onEdit={handleEditUser}
                                onDelete={handleDeleteUser}
                                loading={loading}
                              />
                            </td>
                          );
                        default:
                          // Check if it's a custom column
                          const columnDef = columnDefinitions[columnKey];
                          if (columnDef && columnDef.isCustom) {
                            // Get the value from the user object using the column key
                            const value = user[columnKey] ?? user[columnDef.key] ?? 'לא זמין';

                            // Format the value based on type
                            let displayValue = value;
                            if (value === null || value === undefined || value === '') {
                              displayValue = 'לא זמין';
                            } else if (typeof value === 'boolean') {
                              displayValue = value ? 'כן' : 'לא';
                            } else if (columnDef.type === 'int' || columnDef.type === 'number') {
                              displayValue = value.toString();
                            }

                            return (
                              <td key={columnKey} className={styles.customColumn} title={`${columnDef.label} מ${columnDef.tableName}`}>
                                {displayValue}
                              </td>
                            );
                          }
                          return null;
                      }
                    };

                    return (
                      <tr
                        key={`${user.UserID}-${user.Email}-${index}`}
                        role="row"
                        onClick={() => handleRowClick(user)}
                        className={styles.clickableRow}
                        title="לחץ לצפייה בפרטים"
                      >
                        {columnOrder.map(columnKey =>
                          visibleColumns[columnKey] ? renderCell(columnKey) : null
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className={styles.noResults}>
                      {searchTerm || selectedApartment ? 'לא נמצאו משתמשים התואמים לחיפוש' : 'אין משתמשים'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className={styles.mobileCardsContainer}>
            <div className={styles.cardsGrid} role="grid">
              {currentResidents.length > 0 ? (
                currentResidents.map((user, index) => (
                  <UserCard
                    key={`${user.UserID}-${user.Email}-${index}`}
                    user={user}
                    buildingId={getBuildingId()}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteUser}
                    getRoleDisplay={getRoleDisplay}
                    getRoleColor={getRoleColor}
                  />
                ))
              ) : (
                <div className={styles.emptyState}>
                  {searchTerm || selectedApartment ? 'לא נמצאו משתמשים התואמים לחיפוש' : 'אין משתמשים'}
                </div>
              )}
            </div>
          </div>

          {filteredResidents.length > 0 && (
            <div className={styles.pagination} role="navigation" aria-label="ניווט בין עמודים">
              <div className={styles.paginationControls}>
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                  aria-label="עמוד קודם"
                >
                  <ChevronRight size={20} />
                </button>
                <span className={styles.pageInfo} aria-live="polite">
                  עמוד {currentPage} מתוך {totalPages}
                </span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                  aria-label="עמוד הבא"
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
              <div className={styles.resultsPerPage}>
                <label htmlFor="results-per-page">תוצאות בעמוד:</label>
                <select
                  id="results-per-page"
                  value={residentsPerPage}
                  onChange={(e) => {
                    setResidentsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div className={styles.totalInfo} aria-live="polite">
                מציג {indexOfFirstResident + 1}-{Math.min(indexOfLastResident, filteredResidents.length)} מתוך {filteredResidents.length} משתמשים
              </div>
            </div>
          )}
        </>
      )}

      {/* File Upload Modal */}
      {showFileUpload && (
        <ExcelUpload
          onClose={() => setShowFileUpload(false)}
          onUsersCreated={handleFileUploadComplete}
          buildingId={getBuildingId()}
        />
      )}

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={userDetailsModal.isOpen}
        onClose={() => setUserDetailsModal({ isOpen: false, user: null })}
        user={userDetailsModal.user}
      />

      {/* Smart Deletion Modal */}
      <SmartDeletionModal
        isOpen={smartDeletionModal.isOpen}
        onClose={() => setSmartDeletionModal({ isOpen: false, user: null })}
        user={smartDeletionModal.user}
        buildingId={getBuildingId()}
        apartmentId={smartDeletionModal.user?.ApartmentID}
        onSuccess={handleSmartDeletionSuccess}
      />

      {/* Column Settings Modal */}
      <ColumnSettingsModal
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        visibleColumns={visibleColumns}
        toggleColumn={toggleColumn}
        resetColumns={resetColumns}
        isDragEnabled={isDragEnabled}
        toggleDragEnabled={toggleDragEnabled}
        onOpenRelatedTables={handleOpenRelatedTables}
        customColumns={customColumns}
        onRemoveCustomColumn={handleRemoveCustomColumn}
      />

      {/* Related Tables Modal */}
      <RelatedTablesModal
        isOpen={showRelatedTablesModal}
        onClose={() => setShowRelatedTablesModal(false)}
        onAddColumn={handleAddColumnFromRelatedTable}
        customColumns={customColumns}
      />

      {/* Sort Menu */}
      <SortMenu
        isOpen={sortMenuState.isOpen}
        columnKey={sortMenuState.columnKey}
        columnLabel={sortMenuState.columnKey ? columnDefinitions[sortMenuState.columnKey]?.label : ''}
        sortKey={sortMenuState.columnKey ? columnDefinitions[sortMenuState.columnKey]?.sortKey : null}
        position={sortMenuState.position}
        onClose={closeSortMenu}
        onSort={handleSortSelection}
        onAddSecondarySort={handleAddSecondarySort}
        onClearSort={handleClearSort}
        sortLevels={sortLevels}
        columnSum={sortMenuState.columnKey ? calculateColumnSum(sortMenuState.columnKey) : null}
        isNumericColumn={sortMenuState.columnKey ? ['monthlyAmount', 'floor', 'apartment'].includes(columnDefinitions[sortMenuState.columnKey]?.sortKey) : false}
        onShowSummary={handleShowSummary}
      />

      {/* Summary Modal */}
      <SummaryModal
        isOpen={summaryModal.isOpen}
        columnLabel={summaryModal.columnLabel}
        data={summaryModal.data}
        onClose={() => setSummaryModal({ isOpen: false, columnKey: null, columnLabel: null, data: [] })}
      />

      {/* VaadMonthly Adjustment Modal */}
      {vaadMonthlyModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => !vaadMonthlyModal.isProcessing && setVaadMonthlyModal({ isOpen: false, amount: '', isProcessing: false })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1f2937' }}>
                שינוי סכום ועד בית
              </h3>
              <button
                onClick={() => !vaadMonthlyModal.isProcessing && setVaadMonthlyModal({ isOpen: false, amount: '', isProcessing: false })}
                disabled={vaadMonthlyModal.isProcessing}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '4px',
                  color: '#6b7280'
                }}
              >
                <X size={22} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem' }}>
              <p style={{
                marginTop: 0,
                marginBottom: '1.5rem',
                color: '#4b5563',
                fontSize: '0.95rem',
                lineHeight: 1.5,
                textAlign: 'center'
              }}>
                הזן סכום להוספה או הסרה מסכום הועד החודשי של כל <strong>{tenantsOnly.length}</strong> המשתמשים בבניין
              </p>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor="vaadAmountInput"
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 500,
                    color: '#374151',
                    fontSize: '0.9rem'
                  }}
                >
                  סכום (₪)
                </label>
                <input
                  id="vaadAmountInput"
                  type="number"
                  min="0"
                  step="10"
                  placeholder="הזן סכום"
                  value={vaadMonthlyModal.amount}
                  onChange={(e) => setVaadMonthlyModal(prev => ({ ...prev, amount: e.target.value }))}
                  disabled={vaadMonthlyModal.isProcessing}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    fontSize: '1.1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    textAlign: 'center',
                    fontWeight: 500
                  }}
                />
              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'center',
              padding: '1.25rem 1.5rem',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb'
            }}>
              <button
                onClick={() => handleVaadMonthlyAdjustment('add')}
                disabled={vaadMonthlyModal.isProcessing || !vaadMonthlyModal.amount}
                style={{
                  backgroundColor: '#10b981',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: vaadMonthlyModal.isProcessing || !vaadMonthlyModal.amount ? 'not-allowed' : 'pointer',
                  opacity: vaadMonthlyModal.isProcessing || !vaadMonthlyModal.amount ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  transition: 'all 0.2s'
                }}
              >
                {vaadMonthlyModal.isProcessing ? <Loader size={18} /> : <Plus size={18} />}
                הוסף לסכום
              </button>
              <button
                onClick={() => handleVaadMonthlyAdjustment('subtract')}
                disabled={vaadMonthlyModal.isProcessing || !vaadMonthlyModal.amount}
                style={{
                  backgroundColor: '#ef4444',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: vaadMonthlyModal.isProcessing || !vaadMonthlyModal.amount ? 'not-allowed' : 'pointer',
                  opacity: vaadMonthlyModal.isProcessing || !vaadMonthlyModal.amount ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  transition: 'all 0.2s'
                }}
              >
                {vaadMonthlyModal.isProcessing ? <Loader size={18} /> : <Trash size={18} />}
                הסר מהסכום
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Mobile Search Button - Rendered outside main container */}
    {!showMobileSearch && createPortal(
      <button
        className={styles.mobileSearchButton}
        onClick={() => setShowMobileSearch(true)}
        aria-label="פתח חיפוש"
      >
        <Search size={22} />
        {(searchTerm || selectedApartment) && (
          <span className={styles.searchIndicatorDot}></span>
        )}
      </button>,
      document.body
    )}

    {/* Mobile Search Modal */}
    {showMobileSearch && createPortal(
      <div className={styles.mobileSearchModal}>
        <div className={styles.mobileSearchHeader}>
          <h3>חיפוש וסינון</h3>
          <button
            className={styles.mobileSearchClose}
            onClick={() => setShowMobileSearch(false)}
            aria-label="סגור"
          >
            <X size={24} />
          </button>
        </div>
        <div className={styles.mobileSearchContent}>
          <div className={styles.mobileSearchField}>
            <Search size={20} className={styles.mobileSearchIcon} />
            <input
              type="text"
              placeholder="חיפוש לפי שם..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className={styles.mobileSearchField}>
            <Home size={20} className={styles.mobileSearchIcon} />
            <input
              type="text"
              placeholder="חיפוש לפי דירה..."
              value={selectedApartment}
              onChange={(e) => setSelectedApartment(e.target.value)}
            />
          </div>
          {(searchTerm || selectedApartment) && (
            <button
              className={styles.mobileSearchClear}
              onClick={() => {
                setSearchTerm('');
                setSelectedApartment('');
              }}
            >
              נקה הכל
            </button>
          )}
          <button
            className={styles.mobileSearchApply}
            onClick={() => setShowMobileSearch(false)}
          >
            החל
          </button>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default Users;